import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { subscriptionTiers } from '../data/mock';
import { toast } from 'sonner';

const Subscriptions = () => {
  const navigate = useNavigate();

  const handleSubscribe = (tierName) => {
    if (tierName === 'Seeker') {
      toast.success('Seeker access is always free! Browse books to get started.');
      navigate('/store');
    } else {
      toast.info(`${tierName} subscriptions coming soon! Book purchases include a 30-day Berean trial.`);
      navigate('/store');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge className="bg-amber-600 text-white">Digital Access</Badge>
            <h1 className="text-4xl md:text-6xl font-bold">Choose Your Access Level</h1>
            <p className="text-xl text-gray-300">
              From free Seeker access to comprehensive Church curriculum - find the tier that fits your study needs
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Toggle */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="monthly" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="yearly">
                  Yearly <Badge className="ml-2 bg-green-600 text-white">Save 25%</Badge>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="monthly">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {subscriptionTiers.map((tier) => (
                  <Card key={tier.id} className={`relative flex flex-col ${tier.popular ? 'border-amber-500 border-2 shadow-2xl scale-105' : ''}`}>
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-amber-600 text-white px-4 py-1">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                      <div className="mt-4">
                        {tier.price === 0 ? (
                          <div className="text-5xl font-bold text-gray-900">Free</div>
                        ) : (
                          <>
                            <div className="text-5xl font-bold text-gray-900">${tier.price}</div>
                            <div className="text-gray-600 mt-1">{tier.period}</div>
                          </>
                        )}
                      </div>
                      <CardDescription className="mt-4 text-base">{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <ul className="space-y-3">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className={`w-full ${tier.popular ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                        onClick={() => handleSubscribe(tier.name)}
                      >
                        {tier.buttonText}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="yearly">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {subscriptionTiers.map((tier) => (
                  <Card key={tier.id} className={`relative flex flex-col ${tier.popular ? 'border-amber-500 border-2 shadow-2xl scale-105' : ''}`}>
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-amber-600 text-white px-4 py-1">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                      <div className="mt-4">
                        {tier.price === 0 ? (
                          <div className="text-5xl font-bold text-gray-900">Free</div>
                        ) : (
                          <>
                            <div className="text-5xl font-bold text-gray-900">${tier.yearlyPrice}</div>
                            <div className="text-gray-600 mt-1">per year</div>
                            <div className="text-sm text-green-600 mt-2">
                              Save ${(tier.price * 12) - tier.yearlyPrice}
                            </div>
                          </>
                        )}
                      </div>
                      <CardDescription className="mt-4 text-base">{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <ul className="space-y-3">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className={`w-full ${tier.popular ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                        onClick={() => handleSubscribe(tier.name)}
                      >
                        {tier.buttonText}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">What's Included in Each Tier</h2>
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2">
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">Seeker</th>
                  <th className="text-center p-4 font-semibold bg-amber-50">Berean</th>
                  <th className="text-center p-4 font-semibold">Scholar</th>
                  <th className="text-center p-4 font-semibold">Pastor/Church</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Little Book 2 Web Reader', true, true, true, true],
                  ['Interactive Ebook with Live Appendix Links', false, true, true, true],
                  ['GIT Investigations per Month', '3', 'Unlimited', 'Unlimited', 'Unlimited'],
                  ['Interactive Mind Map', 'Overview', 'Full', 'Full', 'Full'],
                  ['16-Lesson Curriculum', false, false, true, true],
                  ['Comparative Doctrine Investigator', false, false, true, true],
                  ['Scholarly Manuscript Access', false, false, true, true],
                  ['Store Discount', '0%', '10%', '15%', '20%'],
                  ['Group Seats', '1', '1', '1', '25'],
                  ['Monthly Q&A with Mordecai', false, false, true, true],
                ].map(([feature, seeker, berean, scholar, church], idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-4 text-gray-700">{feature}</td>
                    <td className="text-center p-4">
                      {typeof seeker === 'boolean' ? (
                        seeker ? <Check className="h-5 w-5 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                      ) : (
                        <span className="text-gray-700">{seeker}</span>
                      )}
                    </td>
                    <td className="text-center p-4 bg-amber-50">
                      {typeof berean === 'boolean' ? (
                        berean ? <Check className="h-5 w-5 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                      ) : (
                        <span className="text-gray-700 font-medium">{berean}</span>
                      )}
                    </td>
                    <td className="text-center p-4">
                      {typeof scholar === 'boolean' ? (
                        scholar ? <Check className="h-5 w-5 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                      ) : (
                        <span className="text-gray-700">{scholar}</span>
                      )}
                    </td>
                    <td className="text-center p-4">
                      {typeof church === 'boolean' ? (
                        church ? <Check className="h-5 w-5 text-green-600 mx-auto" /> : <span className="text-gray-300">-</span>
                      ) : (
                        <span className="text-gray-700">{church}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Start Your Free Trial Today
            </h2>
            <p className="text-xl text-amber-100">
              Every book purchase includes a 30-day Berean trial. No credit card required.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-700 hover:bg-gray-100"
              onClick={() => navigate('/store')}
            >
              Browse Books & Start Trial
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Subscriptions;
